import { useState } from 'react';
import type { ChatLabels, FeedbackVote, Message } from '../types';
import { cx } from '../utils';
import { ThumbsDownIcon, ThumbsUpIcon } from './icons';

interface FeedbackButtonsProps {
  message: Message;
  labels: ChatLabels;
  onVote: (vote: FeedbackVote, comment?: string) => void;
}

export function FeedbackButtons({ message, labels, onVote }: FeedbackButtonsProps) {
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const acknowledge = () => {
    setAcknowledged(true);
    setTimeout(() => setAcknowledged(false), 2400);
  };

  const handleVote = (vote: FeedbackVote) => {
    const clearing = message.feedback === vote;
    onVote(vote);
    if (clearing) {
      setCommenting(false);
      return;
    }
    if (vote === 'down') {
      setCommenting(true);
    } else {
      setCommenting(false);
      acknowledge();
    }
  };

  const submitComment = () => {
    const trimmed = comment.trim();
    onVote('down', trimmed || undefined);
    setComment('');
    setCommenting(false);
    acknowledge();
  };

  return (
    <div className="cbw-feedback">
      <div className="cbw-feedback__row">
        <button
          type="button"
          className={cx('cbw-iconbtn', 'cbw-iconbtn--sm', message.feedback === 'up' && 'is-active')}
          onClick={() => handleVote('up')}
          aria-pressed={message.feedback === 'up'}
          aria-label={labels.thumbsUp}
          title={labels.thumbsUp}
        >
          <ThumbsUpIcon width={15} height={15} />
        </button>
        <button
          type="button"
          className={cx(
            'cbw-iconbtn',
            'cbw-iconbtn--sm',
            message.feedback === 'down' && 'is-active is-negative',
          )}
          onClick={() => handleVote('down')}
          aria-pressed={message.feedback === 'down'}
          aria-label={labels.thumbsDown}
          title={labels.thumbsDown}
        >
          <ThumbsDownIcon width={15} height={15} />
        </button>
        {acknowledged && (
          <span className="cbw-feedback__thanks" role="status">
            {labels.feedbackThanks}
          </span>
        )}
      </div>

      {commenting && (
        <div className="cbw-feedback__form">
          <label className="cbw-feedback__label" htmlFor={`cbw-fb-${message.id}`}>
            {labels.feedbackPrompt}
          </label>
          <textarea
            id={`cbw-fb-${message.id}`}
            className="cbw-feedback__input"
            rows={2}
            value={comment}
            placeholder={labels.feedbackPlaceholder}
            onChange={(event) => setComment(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submitComment();
              }
              if (event.key === 'Escape') setCommenting(false);
            }}
            autoFocus
          />
          <div className="cbw-feedback__actions">
            <button type="button" className="cbw-btn cbw-btn--ghost" onClick={() => setCommenting(false)}>
              {labels.feedbackSkip}
            </button>
            <button type="button" className="cbw-btn cbw-btn--primary" onClick={submitComment}>
              {labels.feedbackSubmit}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
